/**
 * URL targeting for visual experiments (contract §1).
 *
 *   simple  — glob where `*` matches anything. A pattern with a scheme
 *             (`https://shop.example/product/*`) is compared against the
 *             full URL; a pattern that starts with `/` is compared against
 *             the path (plus query when the pattern itself has a `?`) on ANY
 *             host; anything else (`shop.example/product/*`) is compared
 *             against host + path.
 *   regex   — tested against the full URL (hash stripped).
 *   exact   — equality, no wildcards. A pattern with a scheme is compared
 *             with the full URL (origin + path + query); a pattern that
 *             starts with `/` with the path (plus query when the pattern has
 *             a `?`) on any host; anything else with host + path (+ query).
 *   contains — case-insensitive substring of the full URL (hash stripped).
 *
 * Trailing slashes and the fragment are ignored on both sides. Any matching
 * exclude target wins; with no include targets at all the changeset's
 * `editorUrl` path is used as an exact-path include.
 *
 * Standalone module (no imports) so the unit tests can load it directly.
 */

function stripTrailingSlash(s) {
  return s.length > 1 && s.endsWith('/') ? s.slice(0, -1) : s;
}

/**
 * Normalize an absolute URL into the comparable parts used by matching.
 * Returns null for anything `new URL` cannot parse.
 */
export function normalizeHref(href) {
  let u;
  try {
    u = new URL(String(href));
  } catch (e) {
    return null;
  }
  const host = (u.host || '').toLowerCase();
  const path = stripTrailingSlash(u.pathname || '/') || '/';
  const search = u.search || '';
  const pathNoRoot = path === '/' ? '' : path;
  return {
    host,
    origin: `${u.protocol}//${host}`,
    path: pathNoRoot,                 // '' for the root, never ends with '/'
    pathQuery: `${pathNoRoot}${search}`,
    originPath: `${u.protocol}//${host}${pathNoRoot}`,
    full: `${u.protocol}//${host}${pathNoRoot}${search}`,
    hostPath: `${host}${pathNoRoot}`,
    hostPathQuery: `${host}${pathNoRoot}${search}`
  };
}

function globToRegExp(pattern) {
  const escaped = String(pattern)
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i');
}

function normalizePattern(pattern) {
  let p = String(pattern || '').trim();
  const hash = p.indexOf('#');
  if (hash !== -1) p = p.slice(0, hash);
  // "https://host/" and "/" both mean the root; comparisons use '' for it.
  const q = p.indexOf('?');
  const base = q === -1 ? p : p.slice(0, q);
  const query = q === -1 ? '' : p.slice(q);
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  return { text: `${b}${query}`, hasQuery: q !== -1 };
}

/**
 * `exact`: full URL (origin + path + query) or path equality. Trailing slash
 * and fragment are ignored on both sides, the host case-insensitively.
 */
function matchesExact(pattern, u) {
  const { text, hasQuery } = normalizePattern(pattern);
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) {
    const p = normalizeHref(text);
    return !!p && p.full === u.full;
  }
  if (text.startsWith('/') || text === '') return text === (hasQuery ? u.pathQuery : u.path);
  // host[/path] without a scheme
  const slash = text.indexOf('/');
  const host = (slash === -1 ? text : text.slice(0, slash)).toLowerCase();
  const rest = slash === -1 ? '' : text.slice(slash);
  return `${host}${rest}` === (hasQuery ? u.hostPathQuery : u.hostPath);
}

/** Test one target against an absolute URL. */
export function matchesUrlTarget(target, href) {
  if (!target || !target.pattern) return false;
  const type = String(target.type || 'simple').toLowerCase();
  if (type === 'contains') {
    const h = String(href);
    const i = h.indexOf('#');
    const needle = String(target.pattern).trim().toLowerCase();
    return !!needle && (i === -1 ? h : h.slice(0, i)).toLowerCase().includes(needle);
  }
  if (type === 'regex') {
    try {
      const h = String(href);
      const i = h.indexOf('#');
      return new RegExp(target.pattern).test(i === -1 ? h : h.slice(0, i));
    } catch (e) {
      return false;
    }
  }
  const u = normalizeHref(href);
  if (!u) return false;
  if (type === 'exact') return matchesExact(target.pattern, u);
  const { text, hasQuery } = normalizePattern(target.pattern);
  let subject;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) {
    subject = hasQuery ? u.full : u.originPath;
  } else if (text.startsWith('/') || text === '') {
    subject = hasQuery ? u.pathQuery : u.path;
  } else {
    subject = hasQuery ? u.hostPathQuery : u.hostPath;
  }
  try {
    return globToRegExp(text).test(subject);
  } catch (e) {
    return false;
  }
}

/**
 * Evaluate a changeset's `urlTargets` against the current URL.
 * @param {Array<{include?: boolean, type?: string, pattern: string}>} targets
 * @param {string} href
 * @param {string} [editorUrl] — fallback exact-path include when no include targets exist
 */
export function matchesUrlTargets(targets, href, editorUrl) {
  const list = Array.isArray(targets) ? targets.filter(t => t && t.pattern != null && t.pattern !== '') : [];
  const excludes = list.filter(t => t.include === false);
  const includes = list.filter(t => t.include !== false);
  if (excludes.some(t => matchesUrlTarget(t, href))) return false;
  if (includes.length === 0) {
    if (!editorUrl) return false;
    const e = normalizeHref(editorUrl);
    const u = normalizeHref(href);
    return !!(e && u && e.path === u.path);
  }
  return includes.some(t => matchesUrlTarget(t, href));
}

/** Read a query parameter from an absolute URL (null when absent). */
export function getQueryParam(href, name) {
  try {
    return new URL(String(href)).searchParams.get(name);
  } catch (e) {
    return null;
  }
}

/**
 * Remove one query parameter from the current URL via history.replaceState
 * (no navigation, no reload). Safe no-op when the param is absent or the
 * History API is unavailable.
 */
export function stripQueryParam(win, name) {
  try {
    const u = new URL(win.location.href);
    if (!u.searchParams.has(name)) return false;
    u.searchParams.delete(name);
    const next = `${u.pathname}${u.search}${u.hash}`;
    if (win.history && typeof win.history.replaceState === 'function') {
      win.history.replaceState(win.history.state, '', next);
      return true;
    }
  } catch (e) {
    /* ignore */
  }
  return false;
}
